<template>
    <MyWorkInsightCard
        :experiment-title="experimentTitle"
        :experiment-slug="experimentSlug"
        :title="title"
        :summary="summary"
        :content="content"
        :insight-id="insightId"
        :unknowns="unknowns"
        :show-see-more="showSeeMore"
        :evidence-groups="evidenceGroups"
        :location="location">
        <template v-if="showRating || interactive || chatError" #feedback>
            <MyWorkInsightFeedback
                v-if="showRating || interactive"
                variant="buttons"
                :feedback-value="feedbackValue"
                :feedback-tags="feedbackTags"
                :feedback-comment="feedbackComment"
                :feedback-submitting="feedbackSubmitting || Boolean(feedActions?.isPending(insightId))"
                :submit-error="!!submitError"
                :reason-prompt="feedActions?.enabled() ? 'never' : reasonPrompt"
                :show-rating="showRating"
                :interactive="interactive"
                :dive-deeper-loading="isStartingChat"
                @select="handleFeedbackSelect"
                @submit-feedback="handleSubmitFeedback"
                @dive-deeper="handleDiveDeeper" />
            <span v-if="chatError" role="alert" class="details text-chip-status-overdue-text text-pretty mt-1">{{ chatError }}</span>
        </template>
    </MyWorkInsightCard>
</template>

<script setup lang="ts">
import MyWorkInsightCard from "~vue/components/MyWorkInsightCard.vue";
import { type LabInsightLocation, useLabInsightActions } from "~vue/composables/useLabInsightActions";
import { insightFeedActions } from "~vue/composables/useMyWorkInsightFeed";
import { evidenceSourceCounts, type InsightEvidenceGroup } from "~vue/Labs/evidence";
import MyWorkInsightFeedback, { type ReasonPrompt } from "~vue/Labs/MyWorkInsightFeedback.vue";
import type { LabInsightFeedbackResponse } from "~vue/types/labs";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { inject, onMounted } from "vue";

const emit = defineEmits<{ feedback: [response?: LabInsightFeedbackResponse] }>();

interface Props {
    experimentTitle: string;
    experimentSlug: string;
    title: string;
    summary: string;
    content?: string;
    insightId: string;
    unknowns?: string[];
    showSeeMore?: boolean;
    evidenceGroups?: InsightEvidenceGroup[];
    /** Server-rendered existing feedback (null when there is none), so the card renders its final state on first paint. */
    initialFeedbackValue?: number | null;
    /** Where the card is rendered, for analytics and evidence-chip context. */
    location?: LabInsightLocation;
    /** Offer "Dive Deeper". Off during onboarding, where leaving setup for chat would lose the flow. */
    interactive?: boolean;
    /** Offer the rating buttons. Independent of `interactive`: each surface decides the two separately. */
    showRating?: boolean;
    /** Passed to the rating buttons; onboarding asks for a reason only on a negative. */
    reasonPrompt?: ReasonPrompt;
}

const props = withDefaults(defineProps<Props>(), {
    content: "",
    unknowns: () => [],
    showSeeMore: false,
    initialFeedbackValue: null,
    location: "mywork",
    interactive: true,
    showRating: true,
    reasonPrompt: "always",
});

const {
    feedbackValue,
    feedbackTags,
    feedbackComment,
    feedbackSubmitting,
    submitError,
    handleFeedbackSelect: submitFeedbackSelect,
    handleSubmitFeedback,
    isStartingChat,
    chatError,
    handleStartChat,
} = useLabInsightActions(props.insightId, props.location, {
    // Callers always pass the server-rendered feedback, so a mount-time fetch would only flash the buttons disabled.
    fetchExistingFeedback: false,
    initialFeedbackValue: () => props.initialFeedbackValue,
});

const cardEventData = () => ({
    insight_id: props.insightId,
    experiment_title: props.experimentTitle,
    experiment_slug: props.experimentSlug,
    location: props.location,
    evidence_source_counts: evidenceSourceCounts(props.evidenceGroups ?? []),
});

const feedActions = inject(insightFeedActions, null);

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_SHOWN, cardEventData());
});

function handleDiveDeeper() {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_CLICKED, cardEventData());
    handleStartChat();
}

async function handleFeedbackSelect(value: 0 | 1) {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_FEEDBACK_SUBMITTED, { ...cardEventData(), value });
    if (feedActions?.enabled()) {
        await feedActions.submit({ insight_id: props.insightId, value, location: props.location, comment: null, tags: [] });
        return;
    }
    const response = await submitFeedbackSelect(value);
    if (!submitError.value) {
        emit("feedback", response);
    }
}
</script>
